// @flow

import type { State } from '../travelerInfo';
import type { Action } from '../../state';

type ActionData = {|
  singleDate: ?string, // YYYY-MM-DD or ""
  fromDate: ?string, // YYYY-MM-DD or ""
  toDate: ?string, // YYYY-MM-DD or ""
  attractionPaxAdults: ?number,
  attractionPaxChildren: ?number,
|};

export type AttractionsTravelerInfo = {|
  singleDate: string, // YYYY-MM-DD or ""
  fromDate: string, // YYYY-MM-DD or ""
  toDate: string, // YYYY-MM-DD or ""
  attractionPaxAdults: number,
  attractionPaxChildren: number,
|};

const SET_ATTRACTIONS_TRAVELER_INFO = 'SET_ATTRACTIONS_TRAVELER_INFO';
// Cookie action is recommended because server should decide the actual date set
// to avoid future conflicts between single date and range dates.
const SET_ATTRACTIONS_TRAVELER_INFO_FROM_COOKIE = 'SET_ATTRACTIONS_TRAVELER_INFO_FROM_COOKIE';

const SINGLE_DATE_COOKIE_KEY = 'AD';
const SINGLE_DATE_CLEAR_COOKIE_KEY = 'AC';
const RANGE_DATES_COOKIE_KEY = 'ARD';
const RANGE_DATES_CLEAR_COOKIE_KEY = 'ARC';
const PAX_COOKIE_KEY = 'APAX';

// returns true if the date format is YYYY-MM-DD
// there is some hacky stuff going on here because of toISOString being overridden on the page
const validateDateString = (date: string) => {
  if (date === '') {
    return true;
  }
  const regEx = /^\d{4}-\d{2}-\d{2}$/;
  if (!date.match(regEx)) {
    // Invalid format
    return false;
  }

  const d = new Date(date);
  if (!d.getTime() && d.getTime() !== 0) {
    return false; // Invalid date
  }
  const bits = date.split('-');
  return (
    Number(bits[0]) === d.getUTCFullYear() &&
    Number(bits[1]) === d.getUTCMonth() + 1 &&
    Number(bits[2]) === d.getUTCDate()
  );
};

/**
 * check if we should update the traveler info based on check in data param changes
 * @param state
 * @param data
 * @returns {boolean}
 */
export function shouldUpdateTravelerInfo(
  state: AttractionsTravelerInfo,
  data: ActionData,
): boolean {
  const { singleDate, fromDate, toDate } = data;
  if (singleDate != null && !validateDateString(singleDate)) {
    return false;
  }
  if (fromDate == null && toDate == null) {
    return true;
  }

  if (fromDate == null || toDate == null) {
    return false;
  }

  return !!(validateDateString(fromDate) && validateDateString(toDate) && fromDate <= toDate);
}

const handleSetAttractionsTravelerInfo: (State, ActionData) => State = (
  state: State,
  data: ActionData,
) => {
  const shouldUpdate = state.attractions
    ? shouldUpdateTravelerInfo(state.attractions, data)
    : false;
  if (!shouldUpdate) return state;
  const { singleDate, fromDate, toDate, attractionPaxAdults, attractionPaxChildren } = data;
  const travelerInfo = {
    ...(singleDate ? { singleDate } : {}),
    ...(fromDate ? { fromDate } : {}),
    ...(toDate ? { toDate } : {}),
    ...(attractionPaxAdults || attractionPaxAdults === 0 ? { attractionPaxAdults } : {}),
    ...(attractionPaxChildren || attractionPaxChildren === 0 ? { attractionPaxChildren } : {}),
  };
  return { ...state, attractions: travelerInfo };
};

const parseUnderscoreDateString = (date: string) => {
  if (date === '_') {
    return '';
  }
  const convertedDate = date.replace(/_/g, '-');
  return validateDateString(convertedDate) ? convertedDate : '';
};

const getActionDataFromCookie: () => ActionData = () => {
  // eslint-disable-next-line no-restricted-globals
  const match = document.cookie.match(new RegExp(`(^| )TAUD=([^;]+)`));
  if (!match) {
    return {
      singleDate: '',
      fromDate: '',
      toDate: '',
      attractionPaxAdults: 0,
      attractionPaxChildren: 0,
    };
  }
  const unifiedDates = match[2].split('*');
  let newestTimeStamp: number = 0;
  let singleDate: string = '';
  let fromDate: string = '';
  let toDate: string = '';
  let attractionPaxAdults = 0;
  let attractionPaxChildren = 0;
  unifiedDates.forEach(cookie => {
    const parts = cookie.split('-');
    if (parts && parts.length >= 3) {
      const currentTimeStamp = parseInt(parts[1], 10);
      switch (parts[0]) {
        case SINGLE_DATE_COOKIE_KEY:
          if (currentTimeStamp > newestTimeStamp) {
            newestTimeStamp = currentTimeStamp;
            singleDate = parseUnderscoreDateString(parts[2].split('.')[0]);
            if (fromDate === '' || toDate === '' || fromDate > singleDate || toDate < singleDate) {
              fromDate = singleDate;
              toDate = singleDate;
            }
          }
          break;
        case RANGE_DATES_COOKIE_KEY:
          if (currentTimeStamp > newestTimeStamp) {
            newestTimeStamp = currentTimeStamp;

            const rangeDatesString = parts[2].split('.');
            if (rangeDatesString.length >= 2) {
              [fromDate, toDate] = rangeDatesString;
            }
            fromDate = parseUnderscoreDateString(fromDate);
            toDate = parseUnderscoreDateString(toDate);
            if (singleDate === '' || fromDate > singleDate || toDate < singleDate) {
              singleDate = fromDate;
            }
          }
          break;
        case SINGLE_DATE_CLEAR_COOKIE_KEY:
        case RANGE_DATES_CLEAR_COOKIE_KEY:
          if (currentTimeStamp > newestTimeStamp) {
            newestTimeStamp = currentTimeStamp;
            singleDate = '';
            fromDate = '';
            toDate = '';
          }
          break;
        case PAX_COOKIE_KEY: {
          const attractionPaxString = parts[2].split('.');
          if (parseInt(attractionPaxString[0], 10) > 0) {
            attractionPaxAdults = parseInt(attractionPaxString[0], 10);
          }
          if (parseInt(attractionPaxString[1], 10) > 0) {
            attractionPaxChildren = parseInt(attractionPaxString[1], 10);
          }
          break;
        }
        default:
      }
    }
  });

  return {
    singleDate,
    fromDate,
    toDate,
    attractionPaxAdults,
    attractionPaxChildren,
  };
};

const emptyState = () => Object.freeze({});

export default (state: State = emptyState(), action: Action): State => {
  switch (action.type) {
    case SET_ATTRACTIONS_TRAVELER_INFO:
      return handleSetAttractionsTravelerInfo(state, action.travelerInfo);
    case SET_ATTRACTIONS_TRAVELER_INFO_FROM_COOKIE:
      return handleSetAttractionsTravelerInfo(state, getActionDataFromCookie());
    default:
      return state;
  }
};
